<template>
  <div class="weighin" ref="weighinRef">
    <div class="d-flex reactions-btns items-center gap-2">
      <img :src="icon" class="icons-color-filter" alt="" />
      
      <div class="flex flex-col gap-1">
        <!-- Title above dropdown -->
        <span class="text-[10px] text-gray-400 font-medium">Select who can weigh in (reply, react, comment)</span>
        
        <!-- Dropdown for tier selection -->
        <Dropdown
          v-model="selectedTier"
          :options="tierOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select who can weigh in"
          class="weighin-dropdown min-w-[180px] max-w-[220px]"
          @change="handleTierChange"
          :disabled="disabled"
          :virtualScrollerOptions="{ itemSize: 44 }"
          scrollHeight="280px"
          panelClass="weighin-dropdown-panel">
        
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex items-center gap-2">
            <div v-if="slotProps.value === 'none'" class="w-4 h-4 flex items-center justify-center">
              <img :src="getTierIcon(slotProps.value)" alt="No one" class="w-3 h-3 icons-color-filter" />
            </div>
            <div v-else class="w-4 h-4 flex items-center justify-center tier-icon-small" v-html="getTierIcon(slotProps.value)"></div>
            <span class="text-xs">{{ getTierLabel(slotProps.value) }}</span>
          </div>
          <span v-else class="text-xs opacity-70">{{ slotProps.placeholder }}</span>
        </template>
        
        <template #option="slotProps">
          <div class="flex items-center gap-2 py-1 px-2">
            <div v-if="slotProps.option.value === 'none'" class="w-4 h-4 flex items-center justify-center">
              <img :src="slotProps.option.icon" alt="No one" class="w-3 h-3 icons-color-filter" />
            </div>
            <div v-else class="w-4 h-4 flex items-center justify-center tier-icon-small" v-html="slotProps.option.icon"></div>
            <div class="flex flex-col">
              <span class="text-xs font-medium">{{ slotProps.option.label }}</span>
              <span class="text-[10px] opacity-60 leading-tight">{{ slotProps.option.description }}</span>
            </div>
          </div>
        </template>
      </Dropdown>
      </div>
      
      <!-- Checkbox for default settings -->
      <div v-if="showDefaultCheckbox" class="flex items-center ml-2">
        <Checkbox v-model="checked" binary class="weighin-checkbox mr-1" id="default-settings" />
        <label for="default-settings" class="text-xs">Default</label>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onBeforeMount, onUnmounted, ref } from 'vue';

import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';
import { useStore } from 'vuex';
import { medalSvgs } from '@/assets/medals.js';

const props = defineProps(['post', 'disabled', 'showDefaultCheckbox']);
const emits = defineEmits(['update:addReply']);

const store = useStore();

const checked = ref(false);
const weighinRef = ref(null);
const userdata = ref(null);
const icon = ref('/icon/public.svg');
const selectedTier = ref('bronze');

const toast = useToast();
const loading = ref(false);

const tierOptions = [
  { 
    value: 'none', 
    label: 'No one can weigh in', 
    icon: '/icon/block_red.svg',
    description: 'Disable all reactions and comments'
  },
  { 
    value: 'bronze', 
    label: 'Anyone can weigh in', 
    icon: medalSvgs.bronze,
    description: 'Everyone (0%+ percentile)'
  },
  { 
    value: 'silver', 
    label: 'Silver tier and above', 
    icon: medalSvgs.silver,
    description: 'Silver+ (35%+ percentile)'
  },
  { 
    value: 'gold', 
    label: 'Gold tier and above', 
    icon: medalSvgs.gold,
    description: 'Gold+ (65%+ percentile)'
  },
  { 
    value: 'ruby', 
    label: 'Ruby tier and above', 
    icon: medalSvgs.ruby,
    description: 'Ruby+ (80%+ percentile)'
  },
  { 
    value: 'diamond', 
    label: 'Diamond tier only', 
    icon: medalSvgs.diamond,
    description: 'Diamond only (95%+ percentile)'
  }
];

const getTierIcon = (tierValue) => {
  const tier = tierOptions.find(t => t.value === tierValue);
  return tier ? tier.icon : medalSvgs.bronze;
};

const getTierLabel = (tierValue) => {
  const tier = tierOptions.find(t => t.value === tierValue);
  return tier ? tier.label : 'Anyone can weigh in';
};

const handleTierChange = async () => {
  loading.value = true;
  
  try {
    if (checked.value) {
      // Save as default setting for user
      const resp = await fetch(`/api/users/weigh-in-tier-threshold`, {
        method: 'POST',
        body: JSON.stringify({
          tierThreshold: selectedTier.value,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (resp.status !== 200 && resp.status !== 201) {
        toast.add({
          severity: 'error',
          summary: 'Tier threshold not saved',
          life: 7000,
        });
        return;
      } else {
        toast.add({
          severity: 'success',
          summary: 'Default tier threshold saved',
          life: 7000,
        });
        store.dispatch('getMe');
      }
    } else {
      // Apply to current post only
      localStorage.setItem('tierThresholdPost', selectedTier.value);
      
      // Update icon based on selection
      if (selectedTier.value === 'none') {
        icon.value = '/icon/block.svg';
      } else {
        icon.value = '/icon/person.svg';
      }
      
      toast.add({
        severity: 'success',
        summary: 'Tier requirement applied to this post',
        life: 7000,
      });
    }
  } catch (error) {
    console.log('error', error);
    toast.add({
      severity: 'error',
      summary: 'An unexpected error occurred',
      life: 7000,
    });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  userdata.value = JSON.parse(localStorage.getItem('user'));
  
  // Set the current tier selection from user's settings
  if (userdata.value?.weighInTierThreshold) {
    selectedTier.value = userdata.value.weighInTierThreshold;
  }
});
</script>

<style scoped lang="scss">
.weighin {
  font-size: 14px;
  position: relative;

  &-body {
    overflow: auto !important;
  }

  .text-red {
    color: #dc2626;
  }

  &-btn {
    display: flex;
    align-items: center;
    margin-left: -12px;
    transition: 0.2s ease-in-out;

    &:hover {
      transform: scale(1.1);
    }
  }

  &-btns {
    cursor: pointer;
  }

  &-txt {
    font-size: 12px;
    color: #a1a1aa;
  }

  &-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border-radius: 0 0 12px 12px;
    overflow: hidden;

    @include _639 {
      overflow: unset;
    }

    button:nth-child(1) {
      background: var(--background-primary);
      color: var(--foreground-primary) !important;
      cursor: pointer;

      &:hover {
        background: var(--background-btn-secondary);
      }
    }

    button:nth-child(2) {
      background: #a3e635;
      cursor: pointer;
      color: #09090b;

      &:hover {
        background: #86c027;
      }

      &.disabled {
        pointer-events: none;
        background: var(--background-secondary) !important;
        color: var(--foreground-primary) !important;
      }
    }

    button {
      height: 44px;
      transition: 0.3s;
      display: flex;
      text-align: center;
      font-weight: 500;
      justify-content: center;
      align-items: center;
    }
  }

  &-form {
    padding: 12px 14px;
  }

  &-total {
    font-size: 16px;
    font-family: 'ClashDislay', serif;
  }

  &-item {
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  &-wrp {
    display: grid;
    grid-template-columns: 1fr;

    .weighin-item {
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }

  &-close {
    height: 48px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-right: 20px;
    width: 100%;
    cursor: pointer;
  }

  &-head {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    padding: 12px 25px;

    &-title {
      font-size: 18px;
    }
  }

  &-label {
    font-size: 14px;
  }

  &-price {
    font-size: 9px;
    font-weight: 500;
    font-family: 'ClashDislay', serif;
  }

  &-item-alt {
    padding: 10px 0;
    background: var(--reaction-item-bg);
    border-radius: 10px;

    &-head {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;

      img {
        margin-left: 5px;
        width: 10px;
        height: 10px;
      }
    }
  }

  &-react {
    display: flex;
    align-items: center;
    padding-left: 5px;
    padding-right: 8px;

    &-r {
      width: 100%;
      padding: 0 10px;
    }
  }
}

.weighin--tooltip {
  display: none;
  background: var(--background-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);

  position: absolute;
  width: 420px;
  left: -10%;
  bottom: -50px;
  border-radius: 13px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.3);
  height: fit-content;

  @include _639 {
    position: fixed;
    top: 0 !important;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding-bottom: 80px;
  }

  &.active {
    display: flex;
    flex-direction: column;
    animation: slide 350ms ease-in-out forwards;

    @keyframes slide {
      0% {
        opacity: 0;
        transform: scale(0.1) translateY(0%);
      }

      100% {
        opacity: 1;
        transform: scale(1);
      }
    }
  }
}

@media (max-height: 600px) {
  .weighin--tooltip {
    height: 593px !important;
    bottom: -20px !important;
  }

  .weighin-body {
    overflow: auto !important;
  }
}

@media (max-height: 720px) {
  .weighin--tooltip {
    height: 670px;
  }
}

@media (min-height: 1024px) {
  .weighin--tooltip {
    bottom: -6px !important;
  }
}

:deep(.p-checkbox-box) {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #1d1d21 !important;
  width: 18px !important;
  height: 18px !important;
}

/* Compact dropdown styling */
:deep(.weighin-dropdown) {
  .p-dropdown-panel {
    .p-dropdown-items {
      padding: 2px 0;
      
      .p-dropdown-item {
        padding: 4px 8px;
        min-height: 40px;
        
        &:hover {
          background: var(--background-secondary);
        }
      }
    }
  }
  
  .p-dropdown-trigger {
    width: 2rem;
  }
  
  .p-dropdown-label {
    padding: 0.4rem 0.5rem;
    font-size: 0.75rem;
  }
}

/* Ensure dropdown panel stays within viewport */
:deep(.weighin-dropdown-panel) {
  max-width: 280px !important;
  right: 0 !important;
  left: auto !important;
  transform: none !important;
}

/* Compact tier medal icons */
.tier-icon-small {
  :deep(.tier-medal-icon) {
    width: 0.875rem !important;
    height: 0.875rem !important;
  }
  
  :deep(svg) {
    width: 0.875rem !important;
    height: 0.875rem !important;
  }
}

.icons-color-filter {
  filter: var(--icons-color-filter);
}
</style>
